import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    List,
    ListItemButton,
    ListItemText,
    CircularProgress,
    IconButton,
    Button,
    Radio,
    RadioGroup,
    FormControlLabel,
    Checkbox,
    FormGroup,
    TextField,
    Divider,
    Paper,
    Collapse,
    ListItemIcon,
    Grid,
    Snackbar,
    Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import MedicalServicesIcon from "@mui/icons-material/MedicalServices";
import EventNoteIcon from "@mui/icons-material/EventNote";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import SubdirectoryArrowRightIcon from "@mui/icons-material/SubdirectoryArrowRight";
import axios from "axios";
import { API_URL } from "../../../../Config/api";

const customScrollbar = {
    "&::-webkit-scrollbar": { width: "5px" },
    "&::-webkit-scrollbar-track": { background: "transparent" },
    "&::-webkit-scrollbar-thumb": { background: "#c1c1c1", borderRadius: "10px" },
};

const getNonNullTyOptionKeys = (tyOptions) =>
    tyOptions &&
        typeof tyOptions === "object" &&
        !Array.isArray(tyOptions)
        ? Object.entries(tyOptions)
            .filter(([, definition]) => definition != null)
            .map(([option]) => option)
        : [];

const PackageAdminJobClosure = ({ eventID, accessToken, onClose, onSubmitted }) => {
    const [services, setServices] = useState([]);
    const [expandedServiceId, setExpandedServiceId] = useState(null);
    const [sessionsByService, setSessionsByService] = useState({});
    const [selectedSession, setSelectedSession] = useState(null);

    const [selectedShift, setSelectedShift] = useState(1);
    const [shiftList, setShiftList] = useState([]);

    const [formDetails, setFormDetails] = useState([]);
    const [answers, setAnswers] = useState({});
    const [closureRemark, setClosureRemark] = useState("");
    const [closureRevalidationRemark, setClosureRevalidationRemark] = useState("");

    // State for dynamic dates and times
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [startTime, setStartTime] = useState("");
    const [endTime, setEndTime] = useState("");

    const [loadingServices, setLoadingServices] = useState(false);
    const [loadingSessions, setLoadingSessions] = useState({});
    const [loadingForm, setLoadingForm] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Snackbar State
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    const handleCloseSnackbar = (event, reason) => {
        if (reason === "clickaway") return;
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    // 1. Fetch Services
    useEffect(() => {
        if (eventID) {
            const fetchServices = async () => {
                setLoadingServices(true);
                try {
                    const res = await axios.get(
                        `${API_URL}/packages/service_detailsclr/?eve_id=${eventID}`,
                        { headers: { Authorization: `Bearer ${accessToken}` } },
                    );
                    setServices(res.data || []);
                } catch (error) {
                    console.error("Error fetching services:", error);
                } finally {
                    setLoadingServices(false);
                }
            };
            fetchServices();
        }
    }, [eventID, accessToken]);

    // 2. Handle Service Accordion
    const handleServiceToggle = async (service) => {
        const pocId = service.eve_poc_id;

        if (expandedServiceId === pocId) {
            setExpandedServiceId(null);
            return;
        }
        setExpandedServiceId(pocId);

        if (!sessionsByService[pocId]) {
            setLoadingSessions((prev) => ({ ...prev, [pocId]: true }));
            try {
                const res = await axios.get(
                    `${API_URL}/packages/reval_session_detailsclr/?poc_id=${pocId}`,
                    { headers: { Authorization: `Bearer ${accessToken}` } },
                );
                setSessionsByService((prev) => ({ ...prev, [pocId]: res.data || [] }));
            } catch (error) {
                console.error("Error fetching sessions:", error);
            } finally {
                setLoadingSessions((prev) => ({ ...prev, [pocId]: false }));
            }
        }
    };

    // 3. Handle Session Click
    // 3. Handle Session Click
    const handleSessionClick = async (session) => {
        setSelectedSession(session);

        // Get total shifts
        const totalShifts = Number(session.total_shift) || 1;

        // Create shift list: 1, 2, 3...
        const shifts = Array.from(
            { length: totalShifts },
            (_, index) => index + 1
        );

        setShiftList(shifts);

        const initialShift = shifts[0] || 1;
        setSelectedShift(initialShift);

        // Reset form
        setAnswers({});
        setClosureRemark("");
        setFormDetails([]);

        // Temporary initial values
        setStartDate(session.actual_StartDate_Time?.split(" ")[0] || "");
        setEndDate(session.actual_EndDate_Time?.split(" ")[0] || "");
        setStartTime("");
        setEndTime("");

        await fetchJobClosureForm(session, initialShift);
    };


    const fetchJobClosureForm = async (session, shiftNumber) => {
        setLoadingForm(true);

        // Reset previous shift form
        setAnswers({});
        setClosureRemark("");
        setClosureRevalidationRemark("");
        setFormDetails([]);

        try {
            const res = await axios.get(
                `${API_URL}/packages/job_closure_fetch/`,
                {
                    params: {
                        dtlsID: session.agg_sp_dt_eve_poc_id,
                        shift: shiftNumber,
                    },
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                }
            );

            const apiData = res.data || [];

            // Separate date/time object from questions
            const scheduleData = apiData.find(
                (item) => item.StartDate !== undefined
            );

            const questionsOnly = apiData.filter(
                (item) => item.question_id !== undefined
            );

            // Fetch existing closure remark
            const closureRemarkData = apiData.find((item) => item.closure_remark !== undefined);
            const revalidationRemarkData = apiData.find((item) => item.Revalidation_remark !== undefined);
            if (closureRemarkData) { setClosureRemark(closureRemarkData.closure_remark || ""); }
            if (revalidationRemarkData) { setClosureRevalidationRemark(revalidationRemarkData.Revalidation_remark || ""); }

            if (scheduleData) {
                setStartDate(scheduleData.StartDate || "");
                setEndDate(scheduleData.EndDate || "");
                setStartTime(scheduleData.StartTime || "");
                setEndTime(scheduleData.EndTime || "");

            }

            // setFormDetails(questionsOnly); 
            setFormDetails(questionsOnly);

            // Set existing selected answers from API
            const initialAnswers = {};

            questionsOnly.forEach((q) => {
                const qId = q.question_id;
                const qType = Number(q.question_type);
                const selectedAnswer = q.selected_answer;

                // RADIO (1)
                if (qType === 1) {
                    let selectedValue = "";
                    let nestedAnswer = null;

                    if (
                        selectedAnswer &&
                        typeof selectedAnswer === "object" &&
                        !Array.isArray(selectedAnswer)
                    ) {
                        const selectedKey = Object.keys(selectedAnswer)[0];
                        const savedAnswer = selectedAnswer[selectedKey];
                        const definition = q.ty_options?.[selectedKey];

                        if (savedAnswer && definition) {
                            selectedValue = selectedKey;

                            const nestedValue = savedAnswer.selected_answer;

                            nestedAnswer = {
                                Question: savedAnswer.Question ?? definition.Question,
                                QuestionType: Number(
                                    savedAnswer.QuestionType ?? definition.type
                                ),
                                value: Array.isArray(nestedValue)
                                    ? nestedValue[0] ?? ""
                                    : nestedValue ?? "",
                            };
                        }
                    } else if (Array.isArray(selectedAnswer)) {
                        selectedValue = selectedAnswer[0] ?? "";
                    }

                    initialAnswers[qId] = {
                        value: selectedValue,
                        type: qType,
                        ...(nestedAnswer ? { nestedAnswer } : {}),
                    };
                }

                // DATEPICKER (2)
                else if (qType === 2) {
                    initialAnswers[qId] = {
                        value: Array.isArray(selectedAnswer)
                            ? selectedAnswer[0] ?? ""
                            : typeof selectedAnswer === "string"
                                ? selectedAnswer
                                : "",
                        type: qType,
                    };
                }

                // TEXT (3)
                else if (qType === 3) {
                    let textValue = "";

                    if (Array.isArray(selectedAnswer)) {
                        textValue = selectedAnswer[0] ?? "";
                    } else if (typeof selectedAnswer === "string") {
                        textValue = selectedAnswer;
                    } else if (
                        selectedAnswer &&
                        typeof selectedAnswer === "object"
                    ) {
                        const firstKey = Object.keys(selectedAnswer)[0];
                        const saved = selectedAnswer[firstKey];

                        textValue = Array.isArray(saved?.selected_answer)
                            ? saved.selected_answer[0] ?? ""
                            : saved?.selected_answer ?? "";
                    }

                    initialAnswers[qId] = {
                        value: textValue,
                        type: qType,
                    };
                }

                // CHECKBOX (4): nested question answers
                else if (qType === 4) {
                    const nestedAnswers = {};
                    const selectedKeys = [];

                    if (
                        selectedAnswer &&
                        typeof selectedAnswer === "object" &&
                        !Array.isArray(selectedAnswer)
                    ) {
                        Object.entries(selectedAnswer).forEach(([key, saved]) => {
                            const definition = q.ty_options?.[key];

                            if (!definition || !saved) return;

                            selectedKeys.push(key);

                            const selectedValues = Array.isArray(saved.selected_answer)
                                ? saved.selected_answer
                                : saved.selected_answer != null
                                    ? [saved.selected_answer]
                                    : [];

                            nestedAnswers[key] = {
                                Question: saved.Question ?? definition.Question,
                                QuestionType: Number(
                                    saved.QuestionType ?? definition.type
                                ),
                                value: selectedValues,
                            };
                        });
                    } else if (Array.isArray(selectedAnswer)) {
                        nestedAnswers.value = {
                            Question: q.question,
                            QuestionType: qType,
                            value: selectedAnswer,
                        };
                    }

                    initialAnswers[qId] = {
                        value: selectedKeys,
                        type: qType,
                        nestedAnswers,
                    };
                }
            });

            setAnswers(initialAnswers);

        } catch (error) {
            console.error("Error fetching form:", error);

            let errorMessage = "Failed to fetch form details.";

            if (error.response?.data) {
                if (typeof error.response.data === "string") {
                    errorMessage = error.response.data;
                } else if (error.response.data.message) {
                    errorMessage = error.response.data.message;
                } else if (error.response.data.detail) {
                    errorMessage = error.response.data.detail;
                }
            } else if (error.message) {
                errorMessage = error.message;
            }

            setSnackbar({
                open: true,
                message: errorMessage,
                severity: "error",
            });
        } finally {
            setLoadingForm(false);
        }
    };

    const handleShiftClick = async (shiftNumber) => {
        if (!selectedSession) return;

        setSelectedShift(shiftNumber);

        await fetchJobClosureForm(
            selectedSession,
            shiftNumber
        );
    };
    // const handleSessionClick = async (session) => {
    //     setSelectedSession(session);
    //     setLoadingForm(true);
    //     setAnswers({});
    //     setClosureRemark("");
    //     setFormDetails([]);

    //     // Temporary initial values
    //     setStartDate(session.actual_StartDate_Time?.split(" ")[0] || "");
    //     setEndDate(session.actual_EndDate_Time?.split(" ")[0] || "");
    //     setStartTime("");
    //     setEndTime("");

    //     try {
    //         const formId = session.form_id;

    //         const res = await axios.get(
    //             `${API_URL}/packages/job_closure_fetch/`,
    //             {
    //                 params: {
    //                     dtlsID: session.agg_sp_dt_eve_poc_id,
    //                     shift: Array.isArray(session.completed_shift)
    //                         ? session.completed_shift.length + 1
    //                         : 1,
    //                 },
    //                 headers: {
    //                     Authorization: `Bearer ${accessToken}`,
    //                 },
    //             }
    //         );

    //         const apiData = res.data || [];

    //         // Separate the date/time object from the questions
    //         const scheduleData = apiData.find(
    //             (item) => item.StartDate !== undefined
    //         );

    //         const questionsOnly = apiData.filter(
    //             (item) => item.question_id !== undefined
    //         );

    //         if (scheduleData) {
    //             setStartDate(scheduleData.StartDate || "");
    //             setEndDate(scheduleData.EndDate || "");
    //             setStartTime(scheduleData.StartTime || "");
    //             setEndTime(scheduleData.EndTime || "");
    //         }

    //         setFormDetails(questionsOnly);
    //     } catch (error) {
    //         console.error("Error fetching form:", error);

    //         // API fail par message extract karna
    //         let errorMessage = "Failed to fetch form details.";
    //         if (error.response?.data) {
    //             if (typeof error.response.data === "string")
    //                 errorMessage = error.response.data;
    //             else if (error.response.data.message)
    //                 errorMessage = error.response.data.message;
    //             else if (error.response.data.detail)
    //                 errorMessage = error.response.data.detail;
    //         } else if (error.message) {
    //             errorMessage = error.message;
    //         }

    //         setSnackbar({
    //             open: true,
    //             message: errorMessage,
    //             severity: "error",
    //         });
    //     } finally {
    //         setLoadingForm(false);
    //     }
    // };

    // --- Form Handlers ---
    const handleAnswerChange = (
        qId,
        value,
        qType,
        isNested = false,
        parentId = null,
        nestedQContext = null,
    ) => {
        setAnswers((prev) => {
            const updated = { ...prev };

            if (isNested && parentId) {
                const parent = { ...(updated[parentId] || {}) };

                if (parent.type === 4) {
                    const nestedAnswers = {
                        ...(parent.nestedAnswers || {}),
                    };

                    const nestedKey = String(qId).split("_").pop();
                    const normalizedValue = Array.isArray(value)
                        ? value
                        : value !== "" && value != null
                            ? [value]
                            : [];

                    nestedAnswers[nestedKey] = {
                        Question: nestedQContext?.Question ?? "",
                        QuestionType: qType,
                        value: normalizedValue,
                    };

                    parent.nestedAnswers = nestedAnswers;
                } else {
                    parent.nestedAnswer = {
                        Question: nestedQContext?.Question ?? "",
                        QuestionType: qType,
                        value,
                    };
                }

                updated[parentId] = parent;
            } else {
                updated[qId] = {
                    ...(updated[qId] || {}),
                    value,
                    type: qType,
                };

                if (qType === 1) {
                    delete updated[qId].nestedAnswer;
                }
            }

            return updated;
        });
    };

    // const handleAnswerChange = (
    //     qId,
    //     value,
    //     qType,
    //     isNested = false,
    //     parentId = null,
    //     nestedQContext = null,
    // ) => {
    //     setAnswers((prev) => {
    //         const updated = { ...prev };

    //         if (isNested && parentId) {
    //             if (!updated[parentId]) updated[parentId] = {};
    //             updated[parentId].nestedAnswer = {
    //                 Question: nestedQContext?.Question || qId,
    //                 QuestionType: qType,
    //                 value: value,
    //             };
    //         } else {
    //             updated[qId] = { ...updated[qId], value, type: qType };
    //             if (qType === 1 && updated[qId].nestedAnswer) {
    //                 delete updated[qId].nestedAnswer;
    //             }
    //         }
    //         return updated;
    //     });
    // };

    const handleCheckboxChange = (
        qId,
        option,
        isChecked,
        qType,
        isNested = false,
        parentId = null,
        nestedQContext = null,
    ) => {
        if (isNested && parentId) {
            setAnswers((prev) => {
                const parent = { ...(prev[parentId] || {}) };
                const nestedAnswers = {
                    ...(parent.nestedAnswers || {}),
                };

                const nestedKey = String(qId).split("_").pop();
                const currentValues = Array.isArray(
                    nestedAnswers[nestedKey]?.value
                )
                    ? nestedAnswers[nestedKey].value
                    : [];

                const newValues = isChecked
                    ? currentValues.some((v) => String(v) === String(option))
                        ? currentValues
                        : [...currentValues, option]
                    : currentValues.filter(
                        (v) => String(v) !== String(option)
                    );

                nestedAnswers[nestedKey] = {
                    Question: nestedQContext?.Question ?? "",
                    QuestionType: qType,
                    value: newValues,
                };

                if (!isChecked) {
                    delete nestedAnswers[nestedKey];
                }

                parent.nestedAnswers = nestedAnswers;

                return {
                    ...prev,
                    [parentId]: parent,
                };
            });

            return;
        }

        setAnswers((prev) => {
            const currentValues = Array.isArray(prev[qId]?.value)
                ? prev[qId].value
                : [];

            const newValues = isChecked
                ? currentValues.some((v) => String(v) === String(option))
                    ? currentValues
                    : [...currentValues, option]
                : currentValues.filter(
                    (v) => String(v) !== String(option)
                );

            const nextState = {
                ...(prev[qId] || {}),
                value: newValues,
                type: qType,
            };

            if (qType === 4) {
                const optionDefinition = nestedQContext?.ty_options?.[option];
                const nestedAnswers = { ...(prev[qId]?.nestedAnswers || {}) };

                if (isChecked) {
                    nestedAnswers[option] = {
                        Question: optionDefinition?.Question ?? option,
                        QuestionType: Number(optionDefinition?.type ?? 1),
                        value: nestedAnswers[option]?.value ?? [],
                    };
                } else {
                    delete nestedAnswers[option];
                }

                nextState.nestedAnswers = nestedAnswers;
            }

            return {
                ...prev,
                [qId]: nextState,
            };
        });
    };

    // const handleCheckboxChange = (
    //     qId,
    //     option,
    //     isChecked,
    //     qType,
    //     isNested = false,
    //     parentId = null,
    //     nestedQContext = null,
    // ) => {
    //     setAnswers((prev) => {
    //         let currentVals = [];

    //         if (isNested && parentId) {
    //             currentVals = prev[parentId]?.nestedAnswer?.value || [];
    //         } else {
    //             currentVals = prev[qId]?.value || [];
    //         }

    //         if (!Array.isArray(currentVals)) {
    //             currentVals = [];
    //         }

    //         const newValues = isChecked
    //             ? [...currentVals, option]
    //             : currentVals.filter((v) => v !== option);

    //         return handleAnswerChange(
    //             qId,
    //             newValues,
    //             qType,
    //             isNested,
    //             parentId,
    //             nestedQContext,
    //         );
    //     });
    // };

    // --- Render Questions ---
    const getQuestionOptionDefinition = (question, selectedValue) => {
        const normalizedValue = Array.isArray(selectedValue)
            ? selectedValue.find((value) => value !== "" && value != null)
            : selectedValue;

        if (normalizedValue === "" || normalizedValue == null) return null;

        const optionMaps = [question?.ty_options, question?.type_options];

        for (const optionMap of optionMaps) {
            if (optionMap && typeof optionMap === "object" && !Array.isArray(optionMap)) {
                const match =
                    optionMap[String(normalizedValue)] ??
                    optionMap[normalizedValue];
                if (match && typeof match === "object") return match;
            }
        }

        return null;
    };

    const renderQuestion = (
        q,
        isNested = false,
        parentId = null,
        parentOpt = null,
        index = null,
    ) => {
        const qId =
            q.question_id ||
            q.QuestionID ||
            (parentId ? `${parentId}_${parentOpt}` : Math.random());

        const qType = Number(q.question_type ?? q.type);
        const qText = q.question ?? q.Question;

        let options = [];

        if (
            (qType === 1 || qType === 4) &&
            typeof q.ty_options === "object" &&
            q.ty_options !== null &&
            !Array.isArray(q.ty_options)
        ) {
            options = Object.keys(q.ty_options);
        } else if (Array.isArray(q.ty_options)) {
            options = q.ty_options;
        } else if (Array.isArray(q.type_options)) {
            options = q.type_options;
        } else if (Array.isArray(q.type_option)) {
            options = q.type_option;
        }

        const currentValue = isNested
            ? answers[parentId]?.nestedAnswer?.value ?? ""
            : answers[qId]?.value ?? "";
        const radioValue =
            currentValue !== "" &&
                options.some((option) => String(option) === String(currentValue))
                ? String(currentValue)
                : "";
        const nestedQuestionDefinition = getQuestionOptionDefinition(q, currentValue);

        return (
            <Box
                key={qId}
                sx={{
                    mb: isNested ? 2 : 4,
                    pl: isNested ? 2 : 0,
                    borderLeft: isNested ? "3px solid #93c5fd" : "none",
                    position: "relative",
                }}
            >
                <Typography
                    variant={isNested ? "body1" : "subtitle1"}
                    fontWeight={600}
                    color={isNested ? "#4b5563" : "#111827"}
                    mb={1.5}
                    sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
                >
                    {isNested ? (
                        <SubdirectoryArrowRightIcon
                            fontSize="small"
                            color="disabled"
                            sx={{ mt: 0.3 }}
                        />
                    ) : (
                        <Box
                            component="span"
                            sx={{ color: "#2563eb", fontWeight: 700, minWidth: "24px" }}
                        >
                            Q.
                        </Box>
                    )}
                    <Box component="span" sx={{ pt: isNested ? 0 : 0 }}>
                        {qText}
                    </Box>
                </Typography>

                <Box sx={{ pl: isNested ? 4 : 4 }}>
                    {/* TYPE 1: RADIO */}
                    {qType === 1 && (
                        <RadioGroup
                            row
                            value={radioValue}
                            onChange={(e) =>
                                handleAnswerChange(
                                    qId,
                                    e.target.value,
                                    qType,
                                    isNested,
                                    parentId,
                                    q,
                                )
                            }
                            sx={{ gap: 2 }}
                        >
                            {options.map((opt, i) => (
                                <FormControlLabel
                                    key={i}
                                    value={opt}
                                    control={<Radio size="small" color="primary" />}
                                    label={
                                        <Typography variant="body2" color="#374151">
                                            {opt}
                                        </Typography>
                                    }
                                    sx={{ mr: 0, "& .MuiFormControlLabel-label": { ml: 0.5 } }}
                                />
                            ))}
                        </RadioGroup>
                    )}

                    {/* TYPE 2: DATEPICKER */}
                    {qType === 2 && (
                        <TextField
                            type="date"
                            fullWidth
                            size="small"
                            InputLabelProps={{ shrink: true }}
                            value={currentValue}
                            onChange={(e) =>
                                handleAnswerChange(
                                    qId,
                                    e.target.value,
                                    qType,
                                    isNested,
                                    parentId,
                                    q,
                                )
                            }
                            sx={{
                                mt: 1,
                                bgcolor: "#fff",
                                maxWidth: "300px",
                                borderRadius: 1,
                            }}
                        />
                    )}

                    {/* TYPE 3: TEXT */}
                    {qType === 3 && (
                        <TextField
                            fullWidth
                            size="small"
                            multiline
                            rows={2}
                            placeholder="Type your answer here..."
                            value={currentValue}
                            onChange={(e) =>
                                handleAnswerChange(
                                    qId,
                                    e.target.value,
                                    qType,
                                    isNested,
                                    parentId,
                                    q,
                                )
                            }
                            sx={{ mt: 1, bgcolor: "#fff", borderRadius: 1 }}
                        />
                    )}

                    {/* TYPE 4: CHECKBOX */}
                    {qType === 4 && (
                        <Box>
                            {Object.entries(q.ty_options || {}).map(
                                ([nestedKey, definition]) => {
                                    if (!definition) return null;

                                    const nestedQuestionId = `${qId}_${nestedKey}`;
                                    const nestedType = Number(definition.type ?? 1);
                                    const nestedOptions = Array.isArray(
                                        definition.type_options
                                    )
                                        ? definition.type_options
                                        : [];

                                    const savedNestedValue =
                                        answers[qId]?.nestedAnswers?.[nestedKey]?.value ?? [];
                                    const nestedValue = Array.isArray(savedNestedValue)
                                        ? savedNestedValue
                                        : [];
                                    const isSelected = (answers[qId]?.value ?? []).some(
                                        (v) => String(v) === String(nestedKey)
                                    );

                                    return (
                                        <Box
                                            key={nestedKey}
                                            sx={{
                                                mb: 2,
                                                p: 2,
                                                border: "1px solid #e5e7eb",
                                                borderRadius: 2,
                                                bgcolor: "#f8fafc",
                                            }}
                                        >
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        size="small"
                                                        checked={isSelected}
                                                        onChange={(e) =>
                                                            handleCheckboxChange(
                                                                qId,
                                                                nestedKey,
                                                                e.target.checked,
                                                                qType,
                                                                false,
                                                                null,
                                                                q
                                                            )
                                                        }
                                                    />
                                                }
                                                label={
                                                    <Typography variant="body2" fontWeight={600} color="#374151">
                                                        {nestedKey}
                                                    </Typography>
                                                }
                                            />

                                            {isSelected && (
                                                <Box sx={{ mt: 1.5, pl: 4 }}>
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight={600}
                                                        mb={1}
                                                        color="#4b5563"
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "flex-start",
                                                            gap: 1,
                                                        }}
                                                    >
                                                        <SubdirectoryArrowRightIcon
                                                            fontSize="small"
                                                            color="disabled"
                                                            sx={{ mt: 0.1 }}
                                                        />
                                                        <Box component="span">
                                                            {definition.Question}
                                                        </Box>
                                                    </Typography>

                                                    {nestedType === 1 && (
                                                        <RadioGroup
                                                            row
                                                            value={
                                                                nestedValue[0] ?? ""
                                                            }
                                                            onChange={(e) =>
                                                                handleAnswerChange(
                                                                    nestedQuestionId,
                                                                    e.target.value,
                                                                    nestedType,
                                                                    true,
                                                                    qId,
                                                                    definition
                                                                )
                                                            }
                                                            sx={{ gap: 2 }}
                                                        >
                                                            {nestedOptions.map((opt) => (
                                                                <FormControlLabel
                                                                    key={opt}
                                                                    value={opt}
                                                                    control={<Radio size="small" color="primary" />}
                                                                    label={
                                                                        <Typography variant="body2" color="#374151">
                                                                            {opt}
                                                                        </Typography>
                                                                    }
                                                                    sx={{ mr: 0, "& .MuiFormControlLabel-label": { ml: 0.5 } }}
                                                                />
                                                            ))}
                                                        </RadioGroup>
                                                    )}

                                                    {nestedType === 2 && (
                                                        <TextField
                                                            type="date"
                                                            size="small"
                                                            InputLabelProps={{ shrink: true }}
                                                            value={nestedValue[0] ?? ""}
                                                            onChange={(e) =>
                                                                handleAnswerChange(
                                                                    nestedQuestionId,
                                                                    e.target.value,
                                                                    nestedType,
                                                                    true,
                                                                    qId,
                                                                    definition
                                                                )
                                                            }
                                                        />
                                                    )}

                                                    {nestedType === 3 && (
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            multiline
                                                            rows={2}
                                                            placeholder="Type your answer here..."
                                                            value={nestedValue[0] ?? ""}
                                                            onChange={(e) =>
                                                                handleAnswerChange(
                                                                    nestedQuestionId,
                                                                    e.target.value,
                                                                    nestedType,
                                                                    true,
                                                                    qId,
                                                                    definition
                                                                )
                                                            }
                                                        />
                                                    )}
                                                </Box>
                                            )}
                                        </Box>
                                    );
                                }
                            )}
                        </Box>
                    )}


                    {/* Nested Logic Box */}
                    {qType !== 4 &&
                        nestedQuestionDefinition &&
                        typeof nestedQuestionDefinition === "object" && (
                            <Box
                                mt={2.5}
                                p={2.5}
                                bgcolor="#f8fafc"
                                borderRadius={2}
                                border="1px dashed #cbd5e1"
                            >
                                {renderQuestion(
                                    nestedQuestionDefinition,
                                    true,
                                    qId,
                                    currentValue,
                                )}
                            </Box>
                        )}
                </Box>

                {!isNested && (
                    <Divider sx={{ mt: 4, opacity: 0.5, borderColor: "#e5e7eb" }} />
                )}
            </Box>
        );
    };

    // --- Submit ---
    const handleSubmit = async () => {
        if (!selectedSession) return;
        setSubmitting(true);

        const formattedAnswerSheet = Object.keys(answers).map((qId) => {
            const ansData = answers[qId];

            const result = {
                QuestionID: Number(qId),
                QuestionType: ansData.type,
            };

            // RADIO (1)
            if (ansData.type === 1) {
                if (ansData.nestedAnswer) {
                    const selectedKey = ansData.value;
                    result.selected_answer = {
                        [selectedKey]: {
                            Question: ansData.nestedAnswer.Question,
                            QuestionType: ansData.nestedAnswer.QuestionType,
                            selected_answer: Array.isArray(
                                ansData.nestedAnswer.value
                            )
                                ? ansData.nestedAnswer.value
                                : [ansData.nestedAnswer.value],
                        },
                    };
                } else {
                    result.selected_answer = ansData.value
                        ? [ansData.value]
                        : [];
                }
            }

            // DATEPICKER (2) and TEXT (3)
            else if (ansData.type === 2 || ansData.type === 3) {
                result.selected_answer = ansData.value !== ""
                    ? [ansData.value]
                    : [];
            }

            // CHECKBOX (4), including nested questions
            else if (ansData.type === 4) {
                const nestedAnswers = ansData.nestedAnswers || {};

                if (Object.keys(nestedAnswers).length > 0) {
                    result.selected_answer = {};

                    Object.entries(nestedAnswers).forEach(([key, nested]) => {
                        if (!nested) return;

                        const values = Array.isArray(nested.value)
                            ? nested.value
                            : nested.value !== "" && nested.value != null
                                ? [nested.value]
                                : [];

                        result.selected_answer[key] = {
                            Question: nested.Question,
                            QuestionType: nested.QuestionType,
                            selected_answer: values,
                        };
                    });
                } else {
                    result.selected_answer = Array.isArray(ansData.value)
                        ? ansData.value
                        : [];
                }
            }

            return result;
        });

        // const formattedAnswerSheet = Object.keys(answers).map((qId) => {
        //     const ansData = answers[qId];

        //     const result = {
        //         QuestionID: parseInt(qId),
        //         QuestionType: ansData.type,
        //     };

        //     if (ansData.type === 4) {
        //         result.selected_answer = ansData.value;
        //     } else if (ansData.type === 3 || ansData.type === 2) {
        //         result.selected_answer = [ansData.value];
        //     } else if (ansData.type === 1) {
        //         if (ansData.nestedAnswer) {
        //             result.selected_answer = {
        //                 [ansData.value]: {
        //                     Question: ansData.nestedAnswer.Question,
        //                     QuestionType: ansData.nestedAnswer.QuestionType,
        //                     selected_answer: Array.isArray(ansData.nestedAnswer.value)
        //                         ? ansData.nestedAnswer.value
        //                         : [ansData.nestedAnswer.value],
        //                 },
        //             };
        //         } else {
        //             result.selected_answer = [ansData.value];
        //         }
        //     }
        //     return result;
        // });

        const payload = {
            formID: selectedSession.form_id || 34,
            dtls_ID: selectedSession.agg_sp_dt_eve_poc_id,
            Shift: selectedShift,
            // Shift: selectedSession.total_shift || 1,
            // Shift: Array.isArray(selectedSession.completed_shift)
            //     ? selectedSession.completed_shift.length + 1
            //     : 1,
            prof_session_start_date:
                startDate || selectedSession.actual_StartDate_Time,
            prof_session_end_date: endDate || selectedSession.actual_EndDate_Time,
            prof_session_start_time: startTime || "10:00:00",
            prof_session_end_time: endTime || "11:00:00",
            closure_remark: closureRemark,
            revalidation_remark: closureRevalidationRemark,
            AnswerSheet: formattedAnswerSheet,
        };

        try {
            await axios.put(`${API_URL}/packages/package_revalidation/`, payload, {
                headers: { Authorization: `Bearer ${accessToken}` },
            });

            setSnackbar({
                open: true,
                message: "Job Closure Submitted Successfully!",
                severity: "success",
            });

            if (onSubmitted) onSubmitted();

            setTimeout(() => {
                onClose();
            }, 1500);
        } catch (error) {
            console.error("Error submitting job:", error);

            let errorMessage = "Failed to submit Job Closure. Please try again.";
            if (error.response?.data) {
                if (typeof error.response.data === "string") {
                    errorMessage = error.response.data;
                } else if (error.response.data.message) {
                    errorMessage = error.response.data.message;
                } else if (error.response.data.error) {
                    errorMessage = error.response.data.error;
                } else if (error.response.data.detail) {
                    errorMessage = error.response.data.detail;
                } else {
                    errorMessage = JSON.stringify(error.response.data);
                }
            } else if (error.message) {
                errorMessage = error.message;
            }

            setSnackbar({
                open: true,
                message: errorMessage,
                severity: "error",
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                bgcolor: "#f9fafb",
                borderRadius: 2,
                position: "relative",
            }}
        >
            {/* HEADER */}
            <Box
                sx={{
                    background: "linear-gradient(to right, #09d67d, #09d67d)",
                    px: 3,
                    py: 2,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                }}
            >
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 600,
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                    }}
                >
                    <AssignmentTurnedInIcon /> Job Closure{" "}
                    <Typography
                        component="span"
                        sx={{ fontSize: "14px", fontWeight: 400, opacity: 0.9, ml: 1 }}
                    >
                        | Event: {eventID}
                    </Typography>
                </Typography>
                <IconButton onClick={onClose} sx={{ color: "#fff" }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <Box sx={{ display: "flex", flex: 1, overflow: "hidden" }}>
                {/* LEFT PANEL */}
                <Box
                    sx={{
                        width: "35%",
                        borderRight: "1px solid #e5e7eb",
                        bgcolor: "#ffffff",
                        overflowY: "auto",
                        ...customScrollbar,
                    }}
                >
                    <Box
                        p={2}
                        sx={{
                            position: "sticky",
                            top: 0,
                            bgcolor: "#f8f9fa",
                            zIndex: 1,
                            borderBottom: "1px solid #e5e7eb",
                        }}
                    >
                        <Typography
                            variant="subtitle2"
                            fontWeight={700}
                            color="#4b5563"
                            letterSpacing={0.5}
                        >
                            SELECT SESSION
                        </Typography>
                    </Box>

                    {loadingServices ? (
                        <Box display="flex" justifyContent="center" mt={4}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : (
                        <List sx={{ p: 0 }}>
                            {services.map((srv) => {
                                const isExpanded = expandedServiceId === srv.eve_poc_id;
                                return (
                                    <React.Fragment key={srv.eve_poc_id}>
                                        <ListItemButton
                                            onClick={() => handleServiceToggle(srv)}
                                            sx={{
                                                bgcolor: isExpanded ? "#f3f4f6" : "#fff",
                                                borderBottom: "1px solid #f3f4f6",
                                                "&:hover": { bgcolor: "#f9fafb" },
                                            }}
                                        >
                                            <ListItemIcon sx={{ minWidth: 36 }}>
                                                <MedicalServicesIcon fontSize="small" color="primary" />
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={
                                                    <Typography
                                                        variant="body2"
                                                        fontWeight={600}
                                                        color="#1f2937"
                                                    >
                                                        {srv.sub_srv_name}
                                                    </Typography>
                                                }
                                                secondary={
                                                    <Typography variant="caption" color="#6b7280">
                                                        POC ID: {srv.eve_poc_id}
                                                    </Typography>
                                                }
                                            />
                                            {isExpanded ? (
                                                <ExpandLess color="action" />
                                            ) : (
                                                <ExpandMore color="action" />
                                            )}
                                        </ListItemButton>

                                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                            <Box
                                                sx={{
                                                    bgcolor: "#fafafa",
                                                    borderBottom: "1px solid #e5e7eb",
                                                }}
                                            >
                                                {loadingSessions[srv.eve_poc_id] ? (
                                                    <Box display="flex" justifyContent="center" p={2}>
                                                        <CircularProgress size={20} />
                                                    </Box>
                                                ) : sessionsByService[srv.eve_poc_id]?.length === 0 ? (
                                                    <Typography
                                                        variant="caption"
                                                        display="block"
                                                        textAlign="center"
                                                        p={2}
                                                        color="textSecondary"
                                                    >
                                                        No sessions available
                                                    </Typography>
                                                ) : (
                                                    <List component="div" disablePadding>
                                                        {sessionsByService[srv.eve_poc_id]?.map((ses) => {
                                                            const isSelected =
                                                                selectedSession?.agg_sp_dt_eve_poc_id ===
                                                                ses.agg_sp_dt_eve_poc_id;

                                                            // ✨ NEW LOGIC FOR SHIFTS AND STATUS BAGES ✨
                                                            const totalShifts = ses.total_shift || 0;
                                                            const completedShiftsCount = Array.isArray(
                                                                ses.completed_shift,
                                                            )
                                                                ? ses.completed_shift.length
                                                                : 0;
                                                            const isClosed =
                                                                ses.Session_jobclosure_status === 1;

                                                            return (
                                                                <ListItemButton
                                                                    key={ses.agg_sp_dt_eve_poc_id}
                                                                    onClick={() => handleSessionClick(ses)}
                                                                    sx={{
                                                                        pl: 6,
                                                                        py: 1.5, // Thodi spacing badhai UI clarity ke liye
                                                                        bgcolor: isSelected
                                                                            ? "#eff6ff"
                                                                            : "transparent",
                                                                        borderLeft: isSelected
                                                                            ? "4px solid #3b82f6"
                                                                            : "4px solid transparent",
                                                                        "&:hover": { bgcolor: "#f3f4f6" },
                                                                    }}
                                                                >
                                                                    <ListItemIcon sx={{ minWidth: 30 }}>
                                                                        <EventNoteIcon
                                                                            fontSize="small"
                                                                            color={isSelected ? "primary" : "action"}
                                                                        />
                                                                    </ListItemIcon>
                                                                    <ListItemText
                                                                        primary={
                                                                            <Typography
                                                                                variant="body2"
                                                                                fontWeight={isSelected ? 600 : 500}
                                                                                color={
                                                                                    isSelected ? "#1d4ed8" : "#374151"
                                                                                }
                                                                            >
                                                                                {ses.actual_StartDate_Time}
                                                                            </Typography>
                                                                        }
                                                                        secondary={
                                                                            <Box
                                                                                mt={0.8}
                                                                                display="flex"
                                                                                flexWrap="wrap"
                                                                                gap={1}
                                                                            >
                                                                                {/* Shifts Badge */}
                                                                                <Typography
                                                                                    variant="caption"
                                                                                    sx={{
                                                                                        bgcolor: "#e0e7ff",
                                                                                        color: "#3730a3",
                                                                                        px: 1,
                                                                                        py: 0.3,
                                                                                        borderRadius: 1,
                                                                                        fontWeight: 600,
                                                                                    }}
                                                                                >
                                                                                    Closure: {completedShiftsCount}/
                                                                                    {totalShifts}
                                                                                </Typography>

                                                                                {/* Status Badge */}
                                                                                {/* <Typography
                                          variant="caption"
                                          sx={{
                                            bgcolor: isClosed
                                              ? "#dcfce7"
                                              : "#fef08a",
                                            color: isClosed
                                              ? "#166534"
                                              : "#854d0e",
                                            px: 1,
                                            py: 0.3,
                                            borderRadius: 1,
                                            fontWeight: 600,
                                          }}
                                        >
                                          {isClosed ? "Closed" : "Pending"}
                                        </Typography> */}
                                                                            </Box>
                                                                        }
                                                                    />
                                                                </ListItemButton>
                                                            );
                                                        })}
                                                    </List>
                                                )}
                                            </Box>
                                        </Collapse>
                                    </React.Fragment>
                                );
                            })}
                        </List>
                    )}
                </Box>

                {/* RIGHT PANEL */}
                <Box sx={{ width: "65%", overflowY: "auto", p: 4, ...customScrollbar }}>
                    {!selectedSession ? (
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            height="100%"
                            opacity={0.5}
                        >
                            <AssignmentTurnedInIcon
                                sx={{ fontSize: 72, mb: 2, color: "#9ca3af" }}
                            />
                            <Typography variant="h5" fontWeight={600} color="#4b5563">
                                No Session Selected
                            </Typography>
                            <Typography variant="body1" color="#6b7280" mt={1}>
                                Please select a session from the left panel to begin.
                            </Typography>
                        </Box>
                    ) : loadingForm ? (
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            height="100%"
                        >
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Paper
                            elevation={0}
                            sx={{
                                p: { xs: 3, md: 4 },
                                borderRadius: 3,
                                border: "1px solid #e5e7eb",
                                bgcolor: "#ffffff",
                                boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05)",
                            }}
                        >

                            {/* SHIFT SELECTION */}
                            <Box mb={3}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        gap: 1.5,
                                        flexWrap: "wrap",
                                    }}
                                >
                                    {shiftList.map((shiftNumber) => {
                                        const isSelected = selectedShift === shiftNumber;

                                        return (
                                            <Button
                                                key={shiftNumber}
                                                variant={isSelected ? "contained" : "outlined"}
                                                onClick={() => handleShiftClick(shiftNumber)}
                                                disabled={loadingForm}
                                                sx={{
                                                    minWidth: 100,
                                                    textTransform: "none",
                                                    fontWeight: 600,
                                                    borderRadius: 2,
                                                    ...(isSelected && {
                                                        bgcolor: "#2563eb",
                                                        "&:hover": {
                                                            bgcolor: "#1d4ed8",
                                                        },
                                                    }),
                                                }}
                                            >
                                                Closure {shiftNumber}
                                            </Button>
                                        );
                                    })}
                                </Box>
                            </Box>

                            <Box mb={4}>
                                <Typography
                                    variant="h5"
                                    fontWeight={700}
                                    color="#111827"
                                >
                                    Closure Form
                                </Typography>

                                <Typography
                                    variant="body2"
                                    color="#6b7280"
                                    mt={0.5}
                                >
                                    Fill the details below to complete the job closure process.
                                </Typography>
                            </Box>
                            {/* <Box mb={4}>
                                <Typography variant="h5" fontWeight={700} color="#111827">
                                    Closure Form
                                </Typography>
                                <Typography variant="body2" color="#6b7280" mt={0.5}>
                                    Fill the details below to complete the job closure process.
                                </Typography>
                            </Box> */}

                            {/* ENHANCED DATE & TIME CONFIGURATION SECTION */}
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 3,
                                    mb: 5,
                                    bgcolor: "#f8fafc",
                                    borderRadius: 2,
                                    borderColor: "#e2e8f0",
                                }}
                            >
                                <Typography
                                    variant="subtitle1"
                                    fontWeight={600}
                                    color="#334155"
                                    mb={2.5}
                                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                                >
                                    <CalendarMonthIcon fontSize="small" color="primary" />
                                    Closure Details
                                </Typography>

                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Start Date"
                                            type="date"
                                            fullWidth
                                            InputLabelProps={{ shrink: true }}
                                            size="small"
                                            value={startDate}
                                            onChange={(e) => setStartDate(e.target.value)}
                                            sx={{ bgcolor: "#fff" }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="End Date"
                                            type="date"
                                            fullWidth
                                            InputLabelProps={{ shrink: true }}
                                            size="small"
                                            value={endDate}
                                            onChange={(e) => setEndDate(e.target.value)}
                                            sx={{ bgcolor: "#fff" }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="Start Time"
                                            type="time"
                                            fullWidth
                                            InputLabelProps={{ shrink: true }}
                                            size="small"
                                            value={startTime}
                                            onChange={(e) => setStartTime(e.target.value)}
                                            sx={{ bgcolor: "#fff" }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            label="End Time"
                                            type="time"
                                            fullWidth
                                            InputLabelProps={{ shrink: true }}
                                            size="small"
                                            value={endTime}
                                            onChange={(e) => setEndTime(e.target.value)}
                                            sx={{ bgcolor: "#fff" }}
                                        />
                                    </Grid>
                                </Grid>
                            </Paper>

                            <Box>
                                {formDetails.map((q, index) =>
                                    renderQuestion(q, false, null, null, index),
                                )}
                            </Box>

                            <Box>
                                <Typography
                                    variant="subtitle1"
                                    fontWeight={600}
                                    color="#374151"
                                    mb={1.5}
                                >
                                    Closure Remark
                                </Typography>

                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    placeholder="Add any final remarks or notes here..."
                                    value={closureRemark}
                                    onChange={(e) => setClosureRemark(e.target.value)}
                                    sx={{ bgcolor: "#f9fafb" }}
                                />
                            </Box>

                            <Box mt={3}>
                                <Typography
                                    variant="subtitle1"
                                    fontWeight={600}
                                    color="#374151"
                                    mb={1.5}
                                >
                                    Closure Revalidation Remark
                                </Typography>

                                <TextField
                                    fullWidth
                                    multiline
                                    rows={3}
                                    placeholder="Add remarks for closure revalidation..."
                                    value={closureRevalidationRemark}
                                    onChange={(e) =>
                                        setClosureRevalidationRemark(e.target.value)
                                    }
                                    sx={{ bgcolor: "#f9fafb" }}
                                />
                            </Box>

                            <Box mt={5} display="flex" justifyContent="flex-end">
                                <Button
                                    variant="contained"
                                    onClick={handleSubmit}
                                    disabled={submitting}
                                    sx={{
                                        bgcolor: "#2563eb",
                                        px: 4,
                                        py: 1.2,
                                        textTransform: "none",
                                        fontSize: "15px",
                                        fontWeight: 600,
                                        borderRadius: 2,
                                        boxShadow: "0 4px 6px -1px rgba(37, 99, 235, 0.2)",
                                        "&:hover": { bgcolor: "#1d4ed8" },
                                    }}
                                >
                                    {submitting ? (
                                        <CircularProgress size={24} color="inherit" />
                                    ) : (
                                        "Submit Closure"
                                    )}
                                </Button>
                            </Box>
                        </Paper>
                    )}
                </Box>
            </Box>

            {/* SNACKBAR FOR SUCCESS/ERROR NOTIFICATIONS */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: "100%", fontWeight: 500 }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default PackageAdminJobClosure;
